import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import {
  useCreateClientProject,
  useUpdateClientProject,
  type ClientProject,
  type ProjectPhase,
} from "@/hooks/useClientProjects";
import { toast } from "sonner";
import { useLeads } from "@/hooks/useLeads";
import { QuickCreateLeadDialog } from "./QuickCreateLeadDialog";
import { useQueryClient } from "@tanstack/react-query";

const formSchema = z.object({
  title: z.string().min(1, "Título é obrigatório"),
  description: z.string().optional(),
  client_name: z.string().optional(),
  client_email: z.string().email("Email inválido").optional().or(z.literal("")),
  lead_id: z.string().min(1, "Vincule um lead ao projeto"),
  phase: z.enum(["backlog", "in_progress", "review", "done"]),
  priority: z.coerce.number().min(0).max(3),
  due_date: z.string().optional(),
  budget: z.coerce.number().optional(),
  tags: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface ClientProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: ClientProject | null;
  defaultPhase: ProjectPhase;
  defaultLead?: { id: string; name: string; email: string; message: string };
}

export function ClientProjectDialog({
  open,
  onOpenChange,
  project,
  defaultPhase,
  defaultLead,
}: ClientProjectDialogProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: leads } = useLeads();
  const createProject = useCreateClientProject();
  const updateProject = useUpdateClientProject();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      client_name: "",
      client_email: "",
      lead_id: "",
      phase: defaultPhase,
      priority: 1,
      due_date: "",
      budget: undefined,
      tags: "",
    },
  });

  useEffect(() => {
    if (project) {
      form.reset({
        title: project.title,
        description: project.description || "",
        client_name: project.client_name || "",
        client_email: project.client_email || "",
        lead_id: project.lead_id || "",
        phase: project.phase,
        priority: project.priority,
        due_date: project.due_date || "",
        budget: project.budget || undefined,
        tags: project.tags.join(", "),
      });
    } else {
      form.reset({
        title: defaultLead ? `Projeto - ${defaultLead.name}` : "",
        description: defaultLead?.message || "",
        client_name: defaultLead?.name || "",
        client_email: defaultLead?.email || "",
        lead_id: defaultLead?.id || "",
        phase: defaultPhase,
        priority: 1,
        due_date: "",
        budget: undefined,
        tags: "",
      });
    }
  }, [project, defaultPhase, defaultLead, form]);

  const onSubmit = async (values: FormValues) => {
    const tags = values.tags
      ? values.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean)
      : [];

    const data = {
      title: values.title,
      description: values.description || undefined,
      client_name: values.client_name || undefined,
      client_email: values.client_email || undefined,
      lead_id: values.lead_id || undefined,
      phase: values.phase,
      priority: values.priority,
      due_date: values.due_date || undefined,
      budget: values.budget || undefined,
      tags,
    };

    if (project) {
      await updateProject.mutateAsync({ id: project.id, data });
      onOpenChange(false);
    } else {
      const created = await createProject.mutateAsync(data);
      onOpenChange(false);
      toast.success("Projeto criado! Crie o orçamento agora.", {
        action: {
          label: "Criar Orçamento",
          onClick: () => navigate(`/admin/leads/${values.lead_id}?tab=proposta`),
        },
        duration: 8000,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{project ? "Editar Projeto" : "Novo Projeto"}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título *</FormLabel>
                  <FormControl>
                    <Input placeholder="Nome do projeto" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Descreva o projeto..." rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="client_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome do Cliente</FormLabel>
                    <FormControl>
                      <Input placeholder="João Silva" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="client_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email do Cliente</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="cliente@email.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="lead_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lead *</FormLabel>
                  <div className="flex items-center gap-2">
                    <Select onValueChange={field.onChange} value={field.value || ""}>
                      <FormControl>
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Selecione o lead..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {leads?.map((lead) => (
                          <SelectItem key={lead.id} value={lead.id}>
                            {lead.email ? `${lead.name} - ${lead.email}` : lead.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <QuickCreateLeadDialog
                      onCreated={(id) => {
                        queryClient.invalidateQueries({ queryKey: ["leads"] });
                        field.onChange(id);
                      }}
                    />
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="phase"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fase</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="backlog">Backlog</SelectItem>
                        <SelectItem value="in_progress">Em Progresso</SelectItem>
                        <SelectItem value="review">Revisão</SelectItem>
                        <SelectItem value="done">Concluído</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prioridade</FormLabel>
                    <Select
                      onValueChange={(v) => field.onChange(parseInt(v))}
                      value={field.value.toString()}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="0">Baixa</SelectItem>
                        <SelectItem value="1">Normal</SelectItem>
                        <SelectItem value="2">Alta</SelectItem>
                        <SelectItem value="3">Urgente</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="due_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prazo</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="budget"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Orçamento (R$)</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" placeholder="5000.00" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="tags"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tags (separadas por vírgula)</FormLabel>
                  <FormControl>
                    <Input placeholder="React, TypeScript, Landing Page" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={createProject.isPending || updateProject.isPending}>
                {project ? "Salvar" : "Criar Projeto"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
